import { v4 as uuidv4 } from 'uuid';
import { FC, useEffect, useRef, useState } from "react";
import { Accordion } from "../../../../components/controls/accordion";
import { DictionaryInput } from "../../../../components/controls/dictionaryInput";
import FlexRow from "../../../../components/controls/FlexRow";
import GridWrapper from "../../../../components/controls/GridWrapper";
import { NumberInput } from "../../../../components/controls/inputs";
import { useLotRemain } from "../../../../components/lotSelector/useLotRemain";
import { BaseModalWindow } from "../../../../components/modalWindows/BaseModalWindow";
import { Spinner } from "../../../../components/spiner/Spinner";
import { ICostInfoDTO } from "../../../../libs/coreapi-dto/@types/common";
import { ILotDTO } from "../../../../libs/coreapi-dto/accounting/lot";
import { IInvoiceOutItemCreateDTO, IInvoiceOutItemEditDTO, IInvoiceOutItemViewDTO } from "../../../../libs/coreapi-dto/documents/invoiceOutItem";
import styles from "../styles/index.module.scss";
import { DecimalInputV2, TextAreaInput } from "../../../../components/controls/inputs/BaseInput";
import { useForm } from "../../../../system/hooks/useForm";
import buffer from "buffer";
import { KizBoxEntity } from "../../../Dictionaries/Kiz/KizBoxEntity";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { KizDataProvider } from "../../../../Services/DataProviders/KizDataProvider";
import { IKizBoxDTO, IKizDTO } from "../../../../libs/coreapi-dto/dirs/kiz";
import { KizParsed } from '../../../Dictionaries/Kiz/KizParsed';
import { roundMoneyFormat } from '../../../../system/functions/round';
import { useTranslation } from 'react-i18next';
import { IGoodsWtruCodeGroupDTO } from '../../../../libs/coreapi-dto/dirs/goods';
import { GoodsDataProvider } from '../../../../Services/DataProviders/GoodsDataProvider';

interface IInvoiceOutItemCreatorProps {
    idGlobal: string;
    idInvoiceOutGlobal: string;
    lot: ILotDTO;
    edit?: IInvoiceOutItemEditDTO;
    ok?: (edit: IInvoiceOutItemEditDTO, view: IInvoiceOutItemViewDTO) => void;
    cancel?: () => void;
    scanData?: string;
    isWithdrawalGismtForUnscannedLots: boolean;
}

interface IValidator {
    quantity: number;
}

export const InvoiceOutItemCreator: FC<IInvoiceOutItemCreatorProps> = (props) => {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const remain = useLotRemain(
        props.lot.idGlobal,
        props.idInvoiceOutGlobal,
        () => setIsSubmitting(true),
        () => setIsSubmitting(false)
    );
    const [viewEntity, setViewEntity] = useState<IInvoiceOutItemViewDTO>({} as IInvoiceOutItemViewDTO);
    const initialRetailPriceIncVat = props.lot.retailCostInfo?.priceIncVat ?? 0;
    const [editEntity, setEditEntity] = useState<IInvoiceOutItemEditDTO>({
        idGlobal: props.edit?.idGlobal ?? props.idGlobal,
        idLotGlobal: props.edit?.idLotGlobal ?? props.lot?.idGlobal,
        idGoodsGlobal: props.edit?.idGoodsGlobal ?? props.lot?.goods.idGlobal,
        quantity: props.edit?.quantity ?? 1,
        retailCostInfo: props.edit?.retailCostInfo ?? props.lot?.retailCostInfo,
        scalingRatioName: props.lot?.scalingRatio.displayName,
        supplierCostInfo: props.edit?.supplierCostInfo ?? props.lot?.supplierCostInfo,
        isKiz: props.isWithdrawalGismtForUnscannedLots ? true : props.lot.isKiz,
        kizs: props.edit?.kizs,
        goodsCode: props.lot.goodsCode,
        producer: props.lot.producer.displayName,
        kizBoxes: props.edit?.kizBoxes,
        isKizBox: props.lot.isKizBox,
        isPieceAccountingKizOsu: props.lot.isPieceAccountingKizOsu,
        kizOsuDto: props.lot?.kizOsuDto?.barcode !== undefined ? {...props.lot?.kizOsuDto, idDocumentItem: props.edit?.idGlobal ?? props.idGlobal} : undefined,
        isOsu: !(props.lot?.kizOsuDto?.barcode === undefined),
        suppler: props.edit?.suppler ?? props.lot?.supplier ?? '',
        goodsName: props.lot.goods.displayName,
    } as IInvoiceOutItemEditDTO);

    const [discount, setDiscount] = useState<number>(props.edit?.discountCostInfo?.vat ?? 0);
    const [quantity, setQuantity] = useState<number>(editEntity.quantity ?? 0);

    const appCtx = useAppContext();
    const kizDataProvider = new KizDataProvider(appCtx.coreApiService);
    const goodsDataProvider = new GoodsDataProvider(appCtx.coreApiService);
    const [createItem, setCreateItem] = useState<IInvoiceOutItemCreateDTO>({} as IInvoiceOutItemCreateDTO);

    const [kizGoodsGroup, setKizGoodsGroup] = useState<string>("")

    const isDivided = (props.lot.numerator ?? 1) < (props.lot.denominator ?? 1)
    const isBox = (props.lot.numerator ?? 1) > (props.lot.denominator ?? 1)
    const isKiz = props.lot.isKiz === true

    const { t } = useTranslation();
    const movT = (value: string) => t("documents.movement." + value);
                                    
    useEffect(() => {
        if (props.scanData) {
            let kizParsed = new KizParsed(props.scanData) 
            let scanData = (props.scanData.length === 18) ? ('00' + props.scanData) : props.scanData;
            const scanDataBase64: string = buffer.Buffer.from(scanData, "ascii").toString("base64");
            if (KizBoxEntity.isKizBox(props.scanData)) {
                kizDataProvider.parse(scanDataBase64, (parsedBarcode) => {
                    setCreateItem({
                        idLotGlobal: props.lot?.idGlobal,
                        idGoodsGlobal: props.lot?.goods.idGlobal,
                        retailCostInfo: props.lot.retailCostInfo,
                        supplierCostInfo: props.lot.supplierCostInfo,
                        quantity: 0,
                        isKiz: props.lot.isKiz,
                        kizs: [] as IKizDTO[],
                        kizBoxes: [{ ...parsedBarcode.kizBoxData, idGlobal: uuidv4(), quantity: props.lot.numerator ?? 1 }],
                    } as IInvoiceOutItemCreateDTO);
                })
            }
            else if (kizParsed.isKiz) {
                kizDataProvider.parse(scanDataBase64, (parsedBarcode) => {
                    setCreateItem({
                        idLotGlobal: props.lot?.idGlobal,
                        idGoodsGlobal: props.lot?.goods.idGlobal,
                        retailCostInfo: props.lot.retailCostInfo,
                        supplierCostInfo: props.lot.supplierCostInfo,
                        quantity: 0,
                        isKiz: props.lot.isKiz,
                        kizs: props.lot.isPieceAccountingKizOsu ? [] as IKizDTO[] : [{ ...parsedBarcode.kizData, idGlobal: uuidv4() }],
                        kizBoxes: [] as IKizBoxDTO[],
                        kizOsuDtos: props.lot.isPieceAccountingKizOsu ? [{ ...parsedBarcode.kizData, idGlobal: uuidv4() }] : [] as IKizDTO[]
                    } as IInvoiceOutItemCreateDTO);
                   
                })
            }
        }
    },[])

    useEffect(() => {
        if (!createItem) return;
        setEditEntity({ ...editEntity, kizBoxes: createItem.kizBoxes, kizs: createItem.kizs, kizOsuDtos: createItem.kizOsuDtos })
    },[createItem])

    useEffect(() => {
        const _viewEntity = {
            idGlobal: props.idGlobal,
            lotName: props.lot.displayName,
            quantity: editEntity.quantity,

            retailPrice: editEntity.retailCostInfo?.price,
            retailPriceIncVat: editEntity.retailCostInfo?.priceIncVat,
            retailVat: editEntity.retailCostInfo?.vat,
            retailSum: editEntity.retailCostInfo?.sum,
            retailVatSum: editEntity.retailCostInfo?.vatSum,
            retailSumIncVat: editEntity.retailCostInfo?.sumIncVat,

            gosContractFundingSourseName: props.lot.gosContractFundingSourseName,
            scalingRatioName: editEntity.scalingRatioName,
            supplierPrice: editEntity.supplierCostInfo?.price,
            supplierPriceIncVat: editEntity.supplierCostInfo?.priceIncVat,
            supplierVat: editEntity.supplierCostInfo?.vat,
            supplierSum: editEntity.supplierCostInfo?.sum,
            supplierVatSum: editEntity.supplierCostInfo?.vatSum,
            supplierSumIncVat: editEntity.supplierCostInfo?.sumIncVat,

            discountPrice: editEntity.discountCostInfo?.price ?? 0,
            discountPriceIncVat: editEntity.discountCostInfo?.priceIncVat ?? 0,
            discountVat: editEntity.discountCostInfo?.vat ?? 0,
            discountSum: editEntity.discountCostInfo?.sum ?? 0,
            discountVatSum: editEntity.discountCostInfo?.vatSum ?? 0,
            discountSumIncVat: editEntity.discountCostInfo?.sumIncVat ?? 0,
            isOsu: editEntity?.isOsu,
            supplier: editEntity.suppler,
            countKizScan: editEntity?.isOsu ? 1 : (editEntity?.kizs?.length ?? 0) + (editEntity?.kizBoxes?.length ?? 0) + (editEntity?.kizOsuDtos?.length ?? 0),
            isKiz: props.isWithdrawalGismtForUnscannedLots ? true : editEntity.isKiz === true,
            isKizBox: editEntity.isKizBox ?? false,
            goodsCode: editEntity.goodsCode,
            producer: editEntity.producer,
            goodsName: props.lot.goods.displayName
        } as IInvoiceOutItemViewDTO
        setViewEntity(_viewEntity);

        if (props.scanData && (editEntity?.kizs || editEntity.kizBoxes || editEntity?.kizOsuDtos )) {
            if (isValid()) {
                props.ok?.(editEntity, _viewEntity);
            }
        }

    }, [editEntity]);

    useEffect(() => {
        if (props.edit) {
            setEditEntity(prev => ({
                ...prev,
                kizs: props.edit?.kizs ?? [],
                kizBoxes: props.edit?.kizBoxes ?? [],
                kizOsuDtos: props.edit?.kizOsuDtos ?? [],
            }));
        }
    }, []);

    useEffect(() => {
        props.isWithdrawalGismtForUnscannedLots && !isDivided && !isBox && !isKiz &&
            goodsDataProvider.getWtruCodeGroup(props.lot.goods.idGlobal,
                (wtruGoodsGroup: IGoodsWtruCodeGroupDTO) => {
                    if (wtruGoodsGroup) {
                        setKizGoodsGroup(wtruGoodsGroup.groupName);
                    }
                },
                () => {
                    setKizGoodsGroup("Не определена");
                }
            );
    }, [props.lot.goods.idGlobal])

    const { isValid, errors, setErrors } = useForm<IValidator>({
        validations: {
            quantity: {
                required: {
                    value: editEntity.quantity && editEntity.quantity > 0 ? false : true,
                    message: "Значение должно быть больше 0",
                },
            },
        },
    });

    const handleDiscountChange = (value: number) => {
        if (value > 100) {
            value = 100;
        }
        
        const limitedDiscount = Math.min(value, 100);
        setDiscount(limitedDiscount);
        
        const roundToTwo = (value: number): number => Number(value.toFixed(2));
        const oldPriceIncVat = initialRetailPriceIncVat;
        const oldVat = props.lot.retailCostInfo?.vat ?? 0;
        const oldPrice = props.lot.retailCostInfo?.price ?? 0;

        const supplierOldPriceIncVat = props.lot.supplierCostInfo?.priceIncVat ?? 0;
        const supplierOldVat = props.lot.supplierCostInfo?.vat ?? 0;
        const supplierOldPrice = props.lot.supplierCostInfo?.price ?? 0;
    
        const priceIncVatValue = oldPriceIncVat - (oldPriceIncVat / 100) * limitedDiscount;
        const priceValue = priceIncVatValue/(1 + oldVat/100);
        const vatValue = (priceValue / 100) * oldVat;

        const supplierPriceIncVatValue = supplierOldPriceIncVat;
        const supplierPriceValue = supplierPriceIncVatValue/(1 + supplierOldVat/100);
        const supplierVatValue = (supplierPriceValue / 100) * supplierOldVat;
    
        const retailCostInfo: ICostInfoDTO = {
            price: roundMoneyFormat(priceValue),
            vatPrice: roundMoneyFormat(priceIncVatValue) - roundMoneyFormat(priceValue),
            priceIncVat: roundMoneyFormat(priceIncVatValue),
            vat: oldVat,
            vatSum: roundMoneyFormat(vatValue * quantity),
            sum: roundMoneyFormat((roundMoneyFormat(priceIncVatValue) * quantity)/(oldVat/100+1)),
            sumIncVat: roundMoneyFormat(priceIncVatValue) * quantity,
            adprice: 0,
        };
        
        const priceDiscount = roundMoneyFormat(oldPrice - retailCostInfo.price);
        const priceIncVatDiscount = roundMoneyFormat(oldPriceIncVat - retailCostInfo.priceIncVat);
        const priceWithQuantityDiscount = priceDiscount * quantity;
        const priceIncVatWithQuantityDiscount = priceIncVatDiscount * quantity;
    
        const discountCostInfo: ICostInfoDTO = {
            price: priceDiscount,
            vatPrice: priceIncVatDiscount - priceDiscount,
            priceIncVat: priceIncVatDiscount,
            vat: limitedDiscount,
            vatSum: roundMoneyFormat((priceIncVatWithQuantityDiscount ?? 0) - (priceWithQuantityDiscount ?? 0)),
            sum: roundToTwo(priceWithQuantityDiscount),
            sumIncVat: roundToTwo(priceIncVatWithQuantityDiscount),
            adprice: 0,
        };
        
        setEditEntity({
            ...editEntity,
            quantity: quantity,
            retailCostInfo: { ...retailCostInfo },
            discountCostInfo: { ...discountCostInfo },
            supplierCostInfo: editEntity.supplierCostInfo,
            kizs: editEntity.kizs,
            kizBoxes: editEntity.kizBoxes,
            kizOsuDtos: editEntity.kizOsuDtos
        });
    };

    const handleRetailPriceIncVatChange = (value: number) => {
        const roundToTwo = (value: number): number => Number(value.toFixed(2));
        const oldPriceIncVat = props.lot.retailCostInfo?.priceIncVat ?? 0;
        const oldVat = props.lot.retailCostInfo?.vat ?? 0;
        const oldPrice = props.lot.retailCostInfo?.price ?? 0;

        const supplierOldPriceIncVat = props.lot.supplierCostInfo?.priceIncVat ?? 0;
        const supplierOldVat = props.lot.supplierCostInfo?.vat ?? 0;
        const supplierOldPrice = props.lot.supplierCostInfo?.price ?? 0;

        let newDiscount = 100 - (value * 100) / oldPriceIncVat;
        newDiscount = roundMoneyFormat(newDiscount);
        newDiscount = Math.max(0, Math.min(newDiscount, 100));

        const priceValue = value / (1 + oldVat / 100);
        const vatValue = (priceValue / 100) * oldVat;

        const supplierPriceIncVatValue = supplierOldPriceIncVat;
        const supplierPriceValue = supplierPriceIncVatValue/(1 + supplierOldVat/100);
        const supplierVatValue = (supplierPriceValue / 100) * supplierOldVat;

        const retailCostInfo: ICostInfoDTO = {
            price: roundMoneyFormat(priceValue),
            vatPrice: roundMoneyFormat(value) - roundMoneyFormat(priceValue),
            priceIncVat: roundMoneyFormat(value),
            vat: oldVat,
            vatSum: roundMoneyFormat(vatValue * quantity),
            sum: roundMoneyFormat((roundMoneyFormat(value) * quantity) / (oldVat / 100 + 1)),
            sumIncVat: roundMoneyFormat(value) * quantity,
            adprice: 0,
        };

        const supplierCostInfo: ICostInfoDTO = {
            price: roundMoneyFormat(supplierPriceValue),
            vatPrice: roundMoneyFormat(supplierPriceIncVatValue) - roundMoneyFormat(supplierPriceValue),
            priceIncVat: roundMoneyFormat(supplierPriceIncVatValue),
            vat: supplierOldVat,
            vatSum: roundMoneyFormat(supplierVatValue * quantity),
            sum: roundMoneyFormat((roundMoneyFormat(supplierPriceIncVatValue) * quantity)/(supplierOldVat/100+1)),
            sumIncVat: roundMoneyFormat(supplierPriceIncVatValue) * quantity,
            adprice: 0,
        };

        const priceDiscount = roundMoneyFormat(oldPrice - retailCostInfo.price);
        const priceIncVatDiscount = roundMoneyFormat(oldPriceIncVat - retailCostInfo.priceIncVat);
        const priceWithQuantityDiscount = priceDiscount * quantity;
        const priceIncVatWithQuantityDiscount = priceIncVatDiscount * quantity;

        const discountCostInfo: ICostInfoDTO = {
            price: priceDiscount,
            vatPrice: priceIncVatDiscount - priceDiscount,
            priceIncVat: priceIncVatDiscount,
            vat: newDiscount,
            vatSum: roundMoneyFormat((priceIncVatWithQuantityDiscount ?? 0) - (priceWithQuantityDiscount ?? 0)),
            sum: roundToTwo(priceWithQuantityDiscount),
            sumIncVat: roundToTwo(priceIncVatWithQuantityDiscount),
            adprice: 0,
        };

        setDiscount(newDiscount);
        setEditEntity({
            ...editEntity,
            quantity: quantity,
            retailCostInfo: { ...retailCostInfo },
            discountCostInfo: { ...discountCostInfo },
            // supplierCostInfo: editEntity.supplierCostInfo
            supplierCostInfo: { ...supplierCostInfo },
            kizs: editEntity.kizs,
            kizBoxes: editEntity.kizBoxes,
            kizOsuDtos: editEntity.kizOsuDtos
        });
    };
    
    const handleQuantityChange = (value: number) => {
        setQuantity(value);
        
        const roundToTwo = (value: number): number => Number(value.toFixed(2));
        const oldPriceIncVat = initialRetailPriceIncVat;
        const oldVat = props.lot.retailCostInfo?.vat ?? 0;
        const oldPrice = props.lot.retailCostInfo?.price ?? 0;

        const supplierOldPriceIncVat = props.lot.supplierCostInfo?.priceIncVat ?? 0;
        const supplierOldVat = props.lot.supplierCostInfo?.vat ?? 0;
        const supplierOldPrice = props.lot.supplierCostInfo?.price ?? 0;
    
        const priceIncVatValue = oldPriceIncVat - (oldPriceIncVat / 100) * discount;
        const priceValue = priceIncVatValue/(1 + oldVat/100);
        const vatValue = (priceValue / 100) * oldVat;

        const supplierPriceIncVatValue = supplierOldPriceIncVat;
        const supplierPriceValue = supplierPriceIncVatValue/(1 + supplierOldVat/100);
        const supplierVatValue = (supplierPriceValue / 100) * supplierOldVat;
    
        const retailCostInfo: ICostInfoDTO = {
            price: roundMoneyFormat(priceValue),
            vatPrice: roundMoneyFormat(priceIncVatValue) - roundMoneyFormat(priceValue),
            priceIncVat: roundMoneyFormat(priceIncVatValue),
            vat: oldVat,
            vatSum: roundMoneyFormat(vatValue * value),
            sum: roundMoneyFormat((roundMoneyFormat(priceIncVatValue) * value)/(oldVat/100+1)),
            sumIncVat: roundMoneyFormat(priceIncVatValue) * value,
            adprice: 0,
        };

        const supplierCostInfo: ICostInfoDTO = {
            price: roundMoneyFormat(supplierPriceValue),
            vatPrice: roundMoneyFormat(supplierPriceIncVatValue) - roundMoneyFormat(supplierPriceValue),
            priceIncVat: roundMoneyFormat(supplierPriceIncVatValue),
            vat: supplierOldVat,
            vatSum: roundMoneyFormat(supplierVatValue * value),
            sum: roundMoneyFormat((roundMoneyFormat(supplierPriceIncVatValue) * value)/(supplierOldVat/100+1)),
            sumIncVat: roundMoneyFormat(supplierPriceIncVatValue) * value,
            adprice: 0,
        };
        
        const priceDiscount = roundMoneyFormat(oldPrice - retailCostInfo.price);
        const priceIncVatDiscount = roundMoneyFormat(oldPriceIncVat - retailCostInfo.priceIncVat);
        const priceWithQuantityDiscount = priceDiscount * value;
        const priceIncVatWithQuantityDiscount = priceIncVatDiscount * value;
    
        const discountCostInfo: ICostInfoDTO = {
            price: priceDiscount,
            vatPrice: priceIncVatDiscount - priceDiscount,
            priceIncVat: priceIncVatDiscount,
            vat: discount,
            vatSum: roundMoneyFormat((priceIncVatWithQuantityDiscount ?? 0) - (priceWithQuantityDiscount ?? 0)),
            sum: roundToTwo(priceWithQuantityDiscount),
            sumIncVat: roundToTwo(priceIncVatWithQuantityDiscount),
            adprice: 0,
        };
        
        setEditEntity({
            ...editEntity,
            quantity: value,
            retailCostInfo: { ...retailCostInfo },
            discountCostInfo: { ...discountCostInfo },
            supplierCostInfo: { ...supplierCostInfo },
            kizs: editEntity.kizs,
            kizBoxes: editEntity.kizBoxes,
            kizOsuDtos: editEntity.kizOsuDtos
        });
    };

    return (
        <BaseModalWindow
            header={`Позиция РН — ${props.lot.displayName}`}
            ok={{
                onClick: () => {
                    if (isValid()) {
                        props.ok?.(editEntity, viewEntity);
                    }
                },
            }}
            cancel={{ onClick: () => props.cancel?.() }}
            fullScreen
        >
            {isSubmitting ? (
                <Spinner />
            ) : (
                <div className={styles.container}>
                    <div className={styles.wrapper}>
                        <div>
                            <LotLabel> {props.lot?.displayName} </LotLabel>
                            <FlexRow>
                                <NumberInput label="Доступно для списания" value={remain} disabled />
                                <DictionaryInput label="Ед. измерения" value={props.lot?.scalingRatio.displayName} disabled />
                                <NumberInput label="Цена с НДС без скидки" value={initialRetailPriceIncVat} disabled />
                            </FlexRow>
                            <FlexRow>
                                <NumberInput
                                    required
                                    onFocus={() => setErrors({ ...errors, quantity: undefined })}
                                    error={errors.quantity}
                                    keyBan={true}
                                    label="Количество"
                                    min={0}
                                    max={remain}
                                    value={editEntity.quantity}
                                    onChange={handleQuantityChange}
                                />
                                <DecimalInputV2
                                    label="Скидка, %" 
                                    value={discount > 100 ? 100 : discount} 
                                    onChange={handleDiscountChange}
                                />
                                <DecimalInputV2 
                                    label="Цена с НДС, розн." 
                                    value={editEntity.retailCostInfo?.priceIncVat} 
                                    onChange={handleRetailPriceIncVatChange} 
                                />
                            </FlexRow>
                        </div>
                            {
                                props.isWithdrawalGismtForUnscannedLots && !isDivided && !isBox && !isKiz &&
                                <TextAreaInput
                                    label={movT("kizGoodsGroup") + ":"}
                                    value={kizGoodsGroup}
                                    lineTextArea
                                    disabled
                                />
                            }
                        <Accordion opened={false} caption="" id={uuidv4()} bold>
                            <GridWrapper cols={3}>
                                <NumberInput disabled label="Цена, розн." value={editEntity.retailCostInfo?.price} />
                                <NumberInput disabled label="Цена, пост." value={editEntity.supplierCostInfo?.price} />
                                <NumberInput disabled label="Цена, скид." value={editEntity.discountCostInfo?.price} />
                            </GridWrapper>
                            <GridWrapper cols={3}>
                                <NumberInput disabled label="Цена с НДС, розн." value={editEntity.retailCostInfo?.priceIncVat} />
                                <NumberInput disabled label="Цена с НДС, пост." value={editEntity.supplierCostInfo?.priceIncVat} />
                                <NumberInput disabled label="Цена с НДС, скид." value={editEntity.discountCostInfo?.priceIncVat} />
                            </GridWrapper>
                            <GridWrapper cols={3}>
                                <NumberInput disabled label="НДС, розн." value={editEntity.retailCostInfo?.vat} />
                                <NumberInput disabled label="НДС, пост." value={editEntity.supplierCostInfo?.vat} />
                                <NumberInput disabled label="НДС, скид." value={editEntity.retailCostInfo?.vat} />
                            </GridWrapper>
                            <GridWrapper cols={3}>
                                <NumberInput disabled label="Сумма НДС, розн." value={editEntity.retailCostInfo?.vatSum} />
                                <NumberInput disabled label="Сумма НДС, пост." value={editEntity.supplierCostInfo?.vatSum} />
                                <NumberInput disabled label="Сумма НДС, скид." value={editEntity.discountCostInfo?.vatSum} />
                            </GridWrapper>
                            <GridWrapper cols={3}>
                                <NumberInput disabled label="Сумма, розн." value={editEntity.retailCostInfo?.sum} />
                                <NumberInput disabled label="Сумма, пост." value={editEntity.supplierCostInfo?.sum} />
                                <NumberInput disabled label="Сумма, скид." value={editEntity.discountCostInfo?.sum} />
                            </GridWrapper>
                            <GridWrapper cols={3}>
                                <NumberInput disabled label="Сумма с НДС, розн." value={editEntity.retailCostInfo?.sumIncVat} />
                                <NumberInput disabled label="Сумма с НДС, пост." value={editEntity.supplierCostInfo?.sumIncVat} />
                                <NumberInput disabled label="Сумма с НДС, скид." value={editEntity.discountCostInfo?.sumIncVat} />
                            </GridWrapper>
                        </Accordion>
                    </div>
                </div>
            )}
        </BaseModalWindow>
    );
};

export default InvoiceOutItemCreator;

const LotLabel: FC = (props) => {
    return <h2 className={styles.title}>{props.children}</h2>;
};
